import { useState, useEffect, useRef, useCallback } from 'react';
import {
  authAxios,
  getFirstDayOfMonth,
  getCurrentDate,
} from '../utils/reportUtils';

export const useReportData = () => {
  const [allOrders, setAllOrders] = useState([]);
  // Default: not loading and no dates selected — show empty data until user searches
  const [loading, setLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  // Default dates: today — fetch today's orders by default
  const [dateFrom, setDateFrom] = useState(getCurrentDate());
  const [dateTo, setDateTo] = useState(getCurrentDate());
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const isFetchingRef = useRef(false);
  const abortControllerRef = useRef(null);
  const perPage = 20;

  // Abort any in-progress fetch and reset fetching flags
  const abortFetch = useCallback(() => {
    try {
      abortControllerRef.current?.abort();
    } catch (err) {
      // ignore
    }
    abortControllerRef.current = null;
    isFetchingRef.current = false;
    setLoading(false);
    setIsFetchingMore(false);
  }, []);

  // Fetch a single page of orders
  const fetchOrdersPage = useCallback(
    async (page) => {
      const params = {
        page: page,
        per_page: perPage,
        // 'orders' endpoint (index) might not support start_date/end_date, so we filter matched results client-side
      };

      const res = await authAxios.get('/orders', {
        params,
        signal: abortControllerRef.current?.signal,
      });

      const responseData = res.data?.data;
      const ordersData = Array.isArray(responseData?.data)
        ? responseData.data
        : Array.isArray(responseData)
          ? responseData
          : [];

      // Extract pagination metadata
      const totalCount = responseData?.total;
      const lastPageNum =
        responseData?.last_page ||
        responseData?.meta?.last_page ||
        res.data?.last_page;
      const currentPageNum =
        responseData?.current_page ||
        responseData?.meta?.current_page ||
        res.data?.current_page ||
        page;

      return {
        ordersData,
        totalCount,
        lastPageNum,
        currentPageNum,
        hasMore: lastPageNum
          ? page < lastPageNum
          : ordersData.length === perPage,
      };
    },
    [perPage]
  );

  // Fetch all orders page by page until last page or until date range is exceeded
  const fetchAllOrdersSequentially = useCallback(async () => {
    if (isFetchingRef.current) {
      abortFetch();
    }
    if (!dateFrom || !dateTo) {
      setAllOrders([]);
      setTotal(0);
      setHasMore(false);
      setLoading(false);
      return;
    }

    abortControllerRef.current = new AbortController();
    isFetchingRef.current = true;
    setLoading(true);
    setAllOrders([]);
    setCurrentPage(1);
    setHasMore(true);

    try {
      let allOrdersList = [];
      let page = 1;
      let lastPageNum = null;

      // Convert dates to timestamps for comparison
      const dTo = new Date(dateTo);
      dTo.setHours(23, 59, 59, 999);
      const dFrom = new Date(dateFrom);
      dFrom.setHours(0, 0, 0, 0);

      const filterAndCheckContinue = (data) => {
        const filtered = [];
        let stopFetching = false;

        for (const order of data) {
          const oDate = new Date(order.created_at);

          if (oDate > dTo) {
            continue; // Skip newer
          }

          if (oDate < dFrom) {
            stopFetching = true;
            // Stop if we went past the start date (assuming descending sort)
            // If sort order isn't guaranteed, we might need to check all, but standard is latest first.
            if (page > 1 || data.length > 5) {
              // optimization: stop only if we are sufficiently confident? 
              // For now, let's assume strict latest().
            }
            continue;
          }

          filtered.push(order);
        }
        return { filtered, stopFetching };
      };

      // Fetch first page
      const firstPageData = await fetchOrdersPage(page);

      const { filtered: firstPageFiltered, stopFetching: stopAfterFirst } = filterAndCheckContinue(firstPageData.ordersData);

      allOrdersList = [...firstPageFiltered];
      lastPageNum = firstPageData.lastPageNum;

      setAllOrders(allOrdersList);
      setCurrentPage(page);

      setLoading(false); // Show data as it comes

      // Calculate total based on what we have found so far? 
      // API total is distinct from filtered total. We'll show filtered count.
      setTotal(allOrdersList.length);

      if (!stopAfterFirst && lastPageNum && lastPageNum > 1) {
        setIsFetchingMore(true);
        for (page = 2; page <= lastPageNum; page++) {
          if (abortControllerRef.current?.signal.aborted) break;

          try {
            const pageData = await fetchOrdersPage(page);
            if (pageData.ordersData.length === 0) break;

            const { filtered, stopFetching } = filterAndCheckContinue(pageData.ordersData);

            if (filtered.length > 0) {
              allOrdersList = [...allOrdersList, ...filtered];
              setAllOrders([...allOrdersList]);
              setTotal(allOrdersList.length);
            }

            setCurrentPage(page);

            if (stopFetching) {
              break;
            }
          } catch (err) {
            if (err.name !== 'AbortError') console.error(`Error fetching page ${page}:`, err);
            break;
          }
        }
        setIsFetchingMore(false);
      } else if (firstPageData.hasMore && !lastPageNum && !stopAfterFirst) {
        // Fallback pagination loop
        setIsFetchingMore(true);
        page = 2;
        while (true) {
          if (abortControllerRef.current?.signal.aborted) break;
          try {
            const pageData = await fetchOrdersPage(page);
            if (pageData.ordersData.length === 0) break;

            const { filtered, stopFetching } = filterAndCheckContinue(pageData.ordersData);

            if (filtered.length > 0) {
              allOrdersList = [...allOrdersList, ...filtered];
              setAllOrders([...allOrdersList]);
              setTotal(allOrdersList.length);
            }
            setCurrentPage(page);

            if (stopFetching) break;
            if (pageData.ordersData.length < perPage) break;
            page++;
          } catch (err) {
            if (err.name !== 'AbortError') console.error(`Error fetching page ${page}:`, err);
            break;
          }
        }
        setIsFetchingMore(false);
      }

      setHasMore(false);
      setLastPage(page);
      // Final total update
      setTotal(allOrdersList.length);

    } catch (err) {
      console.error('Error fetching all orders:', err);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [fetchOrdersPage, dateFrom, dateTo, perPage]);

  // On mount: fetch using default dates (today)
  useEffect(() => {
    fetchAllOrdersSequentially();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Note: do not auto-fetch on mount or when dates change.
  // Fetch only occurs when the consumer calls `handleSearch`.

  const handleSearch = useCallback(() => {
    // Only trigger fetch when both dates are provided
    if (!dateFrom || !dateTo) {
      // keep results empty and avoid API call
      setAllOrders([]);
      setTotal(0);
      setHasMore(false);
      return;
    }
    // Ensure any running fetch is stopped and then start from page 1
    abortFetch();
    fetchAllOrdersSequentially();
  }, [dateFrom, dateTo, fetchAllOrdersSequentially, abortFetch]);

  const handleClearFilters = useCallback(() => {
    // Abort any running fetch and reset to today's date, then refetch
    abortFetch();
    const today = getCurrentDate();
    setDateFrom(today);
    setDateTo(today);
    setAllOrders([]);
    setTotal(0);
    setHasMore(true);
    // start fetching for today
    // We need to defer this slightly or just set logic? 
    // State updates are async. It's better to fetch with the 'today' values explicitly passed if feasible,
    // but fetchAllOrdersSequentially uses state 'dateFrom/dateTo'. 
    // The previous implementation had this issue too.
    // However, for linter compliance:
    fetchAllOrdersSequentially();
  }, [abortFetch, fetchAllOrdersSequentially]);

  // Helper to change page externally while aborting any in-progress fetch
  const setPageAndAbort = useCallback(
    (page) => {
      abortFetch();
      setCurrentPage(page);
    },
    [abortFetch]
  );

  return {
    allOrders,
    loading,
    isFetchingMore,
    hasMore,
    dateFrom,
    dateTo,
    setDateFrom,
    setDateTo,
    handleSearch,
    handleClearFilters,
    isFetchingRef,
    total,
    currentPage,
    lastPage,
    abortFetch,
    setPageAndAbort,
  };
};
